import type { PlatformBridge } from "@acto/taskpane/core/platform/messaging/bridge";
import { buildMailEntityEvent } from "@acto/taskpane/core/platform/messaging/events";
import type { PlatformPlugin } from "@acto/taskpane/core/platform/PlatformPlugin";

declare global {
  interface Window {
    Office?: typeof Office;
  }
}

interface MailItemPayload {
  itemId: string | null;
  subject: string | null;
  sender?: {
    displayName: string | null;
    emailAddress: string | null;
  };
  dateTimeCreated: string | null;
}

const OFFICE_READY_TIMEOUT_MS = 15000;

let initializePatched = false;
type OfficeHostInfo = Awaited<ReturnType<typeof Office.onReady>>;

let officeReadyPromise: Promise<OfficeHostInfo> | undefined;

function ensureOfficeInitializePatched() {
  if (initializePatched || typeof Office === "undefined" || typeof window === "undefined") {
    return;
  }

  Office.initialize = function () {
    window.alert = ((message?: unknown) => {
      console.error("Alert triggered", message);
    }) as typeof window.alert;
  };

  initializePatched = true;
}

async function waitForOfficeReady(): Promise<OfficeHostInfo | null> {
  if (typeof Office === "undefined" || typeof Office.onReady !== "function") {
    return null;
  }

  if (!officeReadyPromise) {
    officeReadyPromise = Office.onReady();
  }

  if (typeof window === "undefined") {
    return null;
  }

  const timeoutPromise = new Promise<null>((resolve) => {
    window.setTimeout(() => resolve(null), OFFICE_READY_TIMEOUT_MS);
  });

  return Promise.race([officeReadyPromise, timeoutPromise]).catch(() => null);
}

const OfficePlugin: PlatformPlugin = {
  name: "office",

  async canBoot() {
    if (typeof window === "undefined" || typeof Office === "undefined") {
      return false;
    }

    ensureOfficeInitializePatched();

    let info: OfficeHostInfo | null = null;
    try {
      info = await waitForOfficeReady();
    } catch {
      return false;
    }

    // We only deploy this add-in to Outlook, so any resolved Office host is acceptable.
    return info !== null;
  },

  async boot() {
    if (typeof Office === "undefined") {
      return undefined;
    }

    ensureOfficeInitializePatched();

    const info = await Office.onReady();
    return info;
  },

  registerMessaging(bridge: PlatformBridge) {
    const mailbox = Office.context?.mailbox;
    if (!mailbox?.addHandlerAsync || !mailbox.removeHandlerAsync) {
      console.warn("Office plugin messaging skipped: mailbox handlers unavailable");
      return;
    }

    const readMailItem = (): MailItemPayload | null => {
      const item = mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Message) {
        return null;
      }

      const sender = item.from;
      return {
        itemId: item.itemId ?? null,
        subject: item.subject ?? null,
        sender: sender
          ? {
              displayName: sender.displayName ?? null,
              emailAddress: sender.emailAddress ?? null,
            }
          : undefined,
        dateTimeCreated: item.dateTimeCreated?.toISOString() ?? null,
      };
    };

    const handler = () => {
      const payload = readMailItem();

      bridge.dispatch(
        buildMailEntityEvent({
          emailAddress: payload?.sender?.emailAddress ?? undefined,
        }),
      );
    };

    mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler);
    handler();
  },
};

export default OfficePlugin;
